import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  CheckCircle,
  XCircle,
  Loader2,
  MapPin,
  Receipt,
  Clock,
  User,
  Store,
  Building,
  CheckSquare,
  Square
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * Approvals for first-time customer/distributor visits (KEYNOTE 11) and
 * first-time outlet additions (KEYNOTE 12). Both require bulk approval, so
 * every tab supports select-all and a single approve/reject action.
 */
const Approvals = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('visits');
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [selected, setSelected] = useState({ visits: [], outlets: [], distributors: [], expenses: [] });
  const [processing, setProcessing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [visitsRes, outletsRes, distributorsRes, expensesRes] = await Promise.all([
        api.get('/visits'),
        api.get('/outlets?approval_status=pending'),
        api.get('/distributors?approval_status=pending'),
        api.get('/expenses?status=pending')
      ]);
      setVisits(visitsRes.data.filter((v) => v.status === 'pending'));
      setOutlets(outletsRes.data);
      setDistributors(distributorsRes.data);
      setExpenses(expensesRes.data);
      setSelected({ visits: [], outlets: [], distributors: [], expenses: [] });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggle = (kind, id) => {
    setSelected((prev) => ({
      ...prev,
      [kind]: prev[kind].includes(id) ? prev[kind].filter((x) => x !== id) : [...prev[kind], id]
    }));
  };

  const toggleAll = (kind, rows) => {
    setSelected((prev) => ({
      ...prev,
      [kind]: prev[kind].length === rows.length ? [] : rows.map((r) => r.id)
    }));
  };

  const bulkAct = async (kind, status) => {
    const ids = selected[kind];
    if (!ids.length) {
      toast.error('Select at least one record');
      return;
    }
    const endpoint = {
      visits: '/visits/bulk-approve',
      outlets: '/outlets/bulk-approve',
      distributors: '/distributors/bulk-approve',
      expenses: '/expenses/bulk-approve'
    }[kind];

    setProcessing(true);
    try {
      const { data } = await api.put(endpoint, { ids, status });
      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk action failed');
    } finally {
      setProcessing(false);
    }
  };

  // Shared select-all header + bulk approve/reject bar
  const BulkBar = ({ kind, rows }) => (
    <div className="flex items-center justify-between gap-3 bg-white rounded-2xl border border-slate-100 p-3 sticky top-14 md:top-0 z-10">
      <button
        type="button"
        onClick={() => toggleAll(kind, rows)}
        className="flex items-center gap-2 text-sm font-medium text-slate-700"
        data-testid={`select-all-${kind}`}
      >
        {selected[kind].length === rows.length && rows.length > 0 ? (
          <CheckSquare className="w-5 h-5 text-sky-600" />
        ) : (
          <Square className="w-5 h-5 text-slate-400" />
        )}
        {selected[kind].length > 0 ? `${selected[kind].length} selected` : 'Select all'}
      </button>
      <div className="flex gap-2">
        <Button
          onClick={() => bulkAct(kind, 'approved')}
          disabled={processing || !selected[kind].length}
          className="h-9 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`bulk-approve-${kind}`}
        >
          {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
          Approve
        </Button>
        <Button
          onClick={() => bulkAct(kind, 'rejected')}
          disabled={processing || !selected[kind].length}
          variant="outline"
          className="h-9 rounded-xl border-red-200 text-red-600 hover:bg-red-50"
          data-testid={`bulk-reject-${kind}`}
        >
          <XCircle className="w-4 h-4 mr-2" />
          Reject
        </Button>
      </div>
    </div>
  );

  const SelectableCard = ({ kind, row, children }) => {
    const isSelected = selected[kind].includes(row.id);
    return (
      <button
        type="button"
        onClick={() => toggle(kind, row.id)}
        className={cn(
          'w-full text-left bg-white rounded-2xl border p-4 transition-all',
          isSelected ? 'border-sky-400 ring-2 ring-sky-100' : 'border-slate-100 hover:border-slate-200'
        )}
        data-testid={`${kind}-approval-${row.id}`}
      >
        <div className="flex items-start gap-3">
          {isSelected ? (
            <CheckSquare className="w-5 h-5 text-sky-600 shrink-0 mt-0.5" />
          ) : (
            <Square className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">{children}</div>
        </div>
      </button>
    );
  };

  const EmptyState = ({ label }) => (
    <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-300" />
      <p className="text-slate-500">No pending {label}</p>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  const tabs = [
    { key: 'visits', label: 'Visits', icon: MapPin, count: visits.length },
    { key: 'distributors', label: 'Customers', icon: Building, count: distributors.length },
    { key: 'outlets', label: 'Outlets', icon: Store, count: outlets.length },
    { key: 'expenses', label: 'TADA', icon: Receipt, count: expenses.length },
  ];

  return (
    <Layout>
      <div className="space-y-4 fade-in" data-testid="approvals-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Approvals</h1>
          <p className="text-sm text-slate-500">
            First visits and first-time outlet additions need your approval before they count
          </p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {tabs.map((t) => (
            <div key={t.key} className="bg-white border border-slate-100 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <t.icon className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-medium text-slate-600">{t.label}</span>
              </div>
              <p className={cn('text-2xl font-bold', t.count ? 'text-amber-600' : 'text-slate-300')}>
                {t.count}
              </p>
            </div>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 bg-slate-100 rounded-xl p-1">
            {tabs.map((t) => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className="rounded-lg data-[state=active]:bg-white text-xs sm:text-sm"
                data-testid={`tab-${t.key}`}
              >
                {t.label} ({t.count})
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Visits */}
          <TabsContent value="visits" className="mt-4 space-y-3">
            {visits.length === 0 ? <EmptyState label="visits" /> : (
              <>
                <BulkBar kind="visits" rows={visits} />
                {visits.map((visit) => (
                  <SelectableCard key={visit.id} kind="visits" row={visit}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                          {visit.contact_person || visit.purpose || 'Visit'}
                          {visit.is_first_visit && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                              FIRST VISIT
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-slate-500">{visit.purpose || 'No purpose'}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />{visit.visit_by_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />{formatDate(visit.created_at)}
                          </span>
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                        Pending
                      </span>
                    </div>
                    {visit.is_first_visit && (
                      <p className="text-xs text-violet-600 mt-2">
                        Approving this also approves the new customer/distributor it created.
                      </p>
                    )}
                  </SelectableCard>
                ))}
              </>
            )}
          </TabsContent>

          {/* New customers/distributors */}
          <TabsContent value="distributors" className="mt-4 space-y-3">
            {distributors.length === 0 ? <EmptyState label="customers/distributors" /> : (
              <>
                <BulkBar kind="distributors" rows={distributors} />
                {distributors.map((d) => (
                  <SelectableCard key={d.id} kind="distributors" row={d}>
                    <h3 className="font-medium text-slate-900">{d.name}</h3>
                    <p className="text-sm text-slate-500">
                      {d.code} • Route {d.route} • Zone {d.zone}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />{d.added_by_name || '—'}
                      </span>
                      {d.created_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />{formatDate(d.created_at)}
                        </span>
                      )}
                    </div>
                  </SelectableCard>
                ))}
              </>
            )}
          </TabsContent>

          {/* New outlets */}
          <TabsContent value="outlets" className="mt-4 space-y-3">
            {outlets.length === 0 ? <EmptyState label="outlets" /> : (
              <>
                <BulkBar kind="outlets" rows={outlets} />
                {outlets.map((o) => (
                  <SelectableCard key={o.id} kind="outlets" row={o}>
                    <h3 className="font-medium text-slate-900">{o.outlet_name}</h3>
                    <p className="text-sm text-slate-500">
                      {o.outlet_id}
                      {o.distributor_name && ` • under ${o.distributor_name}`}
                    </p>
                    <p className="text-sm text-slate-500">
                      {[o.city, o.route && `Route ${o.route}`, o.zone && `Zone ${o.zone}`]
                        .filter(Boolean).join(' • ')}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />{o.added_by_name || '—'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />{formatDate(o.created_at)}
                      </span>
                    </div>
                  </SelectableCard>
                ))}
              </>
            )}
          </TabsContent>

          {/* TADA / expenses - selectable, approved in bulk like every other tab */}
          <TabsContent value="expenses" className="mt-4 space-y-3">
            {expenses.length === 0 ? <EmptyState label="TADA claims" /> : (
              <>
                <BulkBar kind="expenses" rows={expenses} />
                {expenses.map((expense) => (
                <SelectableCard key={expense.id} kind="expenses" row={expense}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="font-medium text-slate-900">{expense.expense_number}</h3>
                      <p className="text-sm text-slate-500">
                        {expense.expense_type}
                        {expense.travel_type && ` • ${expense.travel_type}`}
                        {expense.distance_km ? ` • ${expense.distance_km} km` : ''}
                        {expense.mode_of_travel && ` • ${expense.mode_of_travel}`}
                      </p>
                      {expense.is_auto_calculated && (
                        <p className="text-xs text-emerald-600 mt-1">
                          Auto-calculated from punch in/out journey
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />{expense.created_by_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />{formatDate(expense.date)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-amber-600">
                        ₹{expense.amount?.toLocaleString()}
                      </p>
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        Pending
                      </span>
                    </div>
                  </div>
                </SelectableCard>
                ))}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Approvals;
